import { supabase } from '@/integrations/supabase/client';
import type {
  BrandProfile,
  CreateBrandProfileRequest,
  UpdateBrandProfileRequest,
  BrandExtractionResult
} from '@/types';
import { upsertWritingIdentityFromBrandProfile } from '@/api/writingIdentities';

/**
 * Get all brand profiles for the current user
 */
export async function getBrandProfiles(): Promise<BrandProfile[]> {
  const { data, error } = await supabase.functions.invoke('brand-profiles', {
    method: 'GET',
  });

  if (error) {
    throw new Error(error.message || 'Failed to fetch brand profiles');
  }

  return data;
}

/**
 * Get a specific brand profile by ID
 */
export async function getBrandProfile(id: string): Promise<BrandProfile> {
  const { data, error } = await supabase.functions.invoke(`brand-profiles/${id}`, {
    method: 'GET',
  });

  if (error) {
    throw new Error(error.message || 'Failed to fetch brand profile');
  }

  return data;
}

/**
 * Create a new brand profile
 */
export async function createBrandProfile(profileData: CreateBrandProfileRequest): Promise<BrandProfile> {
  const { data, error } = await supabase.functions.invoke('brand-profiles', {
    method: 'POST',
    body: profileData,
  });

  if (error) {
    throw new Error(error.message || 'Failed to create brand profile');
  }

  const created = data as BrandProfile;
  void upsertWritingIdentityFromBrandProfile(created);
  return created;
}

/**
 * Update an existing brand profile
 */
export async function updateBrandProfile(id: string, profileData: UpdateBrandProfileRequest): Promise<BrandProfile> {
  const { data, error } = await supabase.functions.invoke(`brand-profiles/${id}`, {
    method: 'PUT',
    body: profileData,
  });

  if (error) {
    throw new Error(error.message || 'Failed to update brand profile');
  }

  const updated = data as BrandProfile;
  void upsertWritingIdentityFromBrandProfile(updated);
  return updated;
}

/**
 * Delete a brand profile
 */
export async function deleteBrandProfile(id: string): Promise<void> {
  const { error } = await supabase.functions.invoke(`brand-profiles/${id}`, {
    method: 'DELETE',
  });

  if (error) {
    throw new Error(error.message || 'Failed to delete brand profile');
  }
}

/**
 * Analyze brand content from URLs and text samples using AI
 */
export async function analyzeBrandContent(
  urls: string[],
  textSamples: string[],
  languageCue?: string,
  sitemapUrls?: string[],
): Promise<BrandExtractionResult> {
  try {
    // Filter out empty values
    const validUrls = urls.filter(url => url.trim());
    const validTextSamples = textSamples.filter(sample => sample.trim());

    if (validUrls.length === 0 && validTextSamples.length === 0) {
      throw new Error('Please provide at least one URL or text sample for analysis');
    }

    const requestBody: any = {
      urls: validUrls,
      textSamples: validTextSamples,
      sitemapUrls: (sitemapUrls || []).filter((url) => url.trim()).slice(0, 10),
    };

    // Add language cue if provided
    if (languageCue && languageCue.trim()) {
      requestBody.languageCue = languageCue.trim();
    }

    const { data, error } = await supabase.functions.invoke('analyze-brand', {
      body: requestBody,
    });

    if (error) {
      console.error('[analyzeBrandContent] Error:', error);
      throw new Error(error.message || 'Failed to analyze brand content');
    }

    return data;
  } catch (error) {
    console.error('[analyzeBrandContent] Error:', error);
    throw error;
  }
}

/**
 * Toggle brand profile active status
 */
export async function toggleBrandProfileStatus(id: string, isActive: boolean): Promise<BrandProfile> {
  return updateBrandProfile(id, { is_active: isActive });
}

/**
 * Set default purpose for a brand profile
 */
export async function setDefaultBrandPurpose(id: string, purpose: string): Promise<BrandProfile> {
  return updateBrandProfile(id, { default_purpose: purpose || null });
}
